import {onBeforeUnmount, type Ref} from 'vue';
import {useDebounce} from './useDebounce';

/**
 *
 * @param options - Options to configure useSplitScreenFromDrawer for each case
 * @param {Ref} options.areaToMakeScrollable - The background DOM area you need to make scrollable when the drawer is open
 */
export function useSplitScreenFromDrawer(options: {areaToMakeScrollable: Ref<HTMLElement>}) {
    const {debounce} = useDebounce();

    const {areaToMakeScrollable} = options;
    const scrollAreaWidth = 1200;
    const innerScrollableContent = areaToMakeScrollable.value;

    if (!innerScrollableContent) console.warn('Given areaToMakeScrollable ref is not valid');

    /** For window resize, performance optimization */
    function initLayoutCompute(): void {
        debounce(() => {
            computeRemainingSpaceBetweenNavbarAndDrawer(true);
        }, 250);
    }

    /** We automatically create the DOM to handle the scrollable area, to reduce the boilerplate for each implementation */
    const {contentWrapper} = wrapContentInParent();

    function wrapContentInParent() {
        const contentWrapper = document.createElement('div');
        contentWrapper.classList.add('split-screen-wrapper');

        // insert wrapper before el in the DOM tree
        innerScrollableContent.parentNode!.insertBefore(contentWrapper, innerScrollableContent);

        // move el into wrapper. Probably costly and better to do
        contentWrapper.appendChild(innerScrollableContent);

        return {
            contentWrapper,
        };
    }

    async function computeRemainingSpaceBetweenNavbarAndDrawer(resize = false) {
        const drawerElement = document.querySelector('.joy-drawer--wrapper-open');
        const navbarElement = document.querySelector('.aside-main_actions');

        if (!drawerElement) {
            return;
        }

        if (!resize) {
            // on resize we don't need this check
            // The drawer is animated when displayed. We need to wait for the end of the animation otherwise the drawer calculated width returns 0.
            await waitForAnimationEnd(drawerElement as HTMLElement);
        }

        const windowWidth = document.body.clientWidth;
        const navbarWidth = (navbarElement as HTMLElement).offsetWidth || 72;
        const subtractorWidth = (drawerElement as HTMLElement).offsetWidth || 780;

        contentWrapper.style.width = windowWidth - subtractorWidth - navbarWidth + 'px';
        contentWrapper.style.overflowX = 'auto';

        innerScrollableContent.style.width = scrollAreaWidth + 'px';
    }

    function waitForAnimationEnd(element: HTMLElement) {
        return new Promise((resolve: any) => {
            element.addEventListener(
                'animationend',
                () => {
                    resolve();
                },
                {once: true},
            );
        });
    }

    function resetSplitScreenBehavior() {
        contentWrapper.style.width = '';
        contentWrapper.style.overflowX = '';
        innerScrollableContent.style.width = '';
    }

    function addResizeEventListener() {
        window.addEventListener('resize', initLayoutCompute);
    }

    function removeResizeEventListener() {
        window.removeEventListener('resize', initLayoutCompute);
    }

    onBeforeUnmount(() => {
        removeResizeEventListener();
    });

    return {
        addResizeEventListener,
        computeRemainingSpaceBetweenNavbarAndDrawer,
        removeResizeEventListener,
        resetSplitScreenBehavior,
    };
}
